'use client';

import Image from 'next/image';
import { CameraIcon } from '@heroicons/react/20/solid';

import { Text } from '@northladder/i18n';
import { TAssessmentImageType, TImageMetaPositionAlt } from '@northladder/apis';

import { apiTranslations } from '../../translations';

import { IDeviceImageOptionsWithMetadataTranslations } from '../types';
import { useRouteURL, useTargetAssessmentConfig } from '../hooks';

type TPendingUploadingV2Props = Pick<
  IDeviceImageOptionsWithMetadataTranslations,
  'title' | 'titlePostfix'
> & {
  imageId: TAssessmentImageType;
  position: TImageMetaPositionAlt;
};

/**
 * -----------------------------------------------------------------------------
 * Displays that the image has is yet to be uploaded.
 * It will show only the upload status is not `COMPLETED`
 *
 * - See also `DoneUploadingV2` & `PendingReUpload`
 */
export function PendingUploadingV2({
  imageId,
  position,
  title,
  titlePostfix,
}: TPendingUploadingV2Props) {
  const { navigateToRoute, assessmentType } = useRouteURL();

  const { thumbnail, screen } = useTargetAssessmentConfig(
    assessmentType === 'APPLE' ? imageId : position
  );

  const handleClick = () => {
    navigateToRoute(`/${screen}`, 'push');
  };

  return (
    <button
      className="
        space-between xsm:flex-1 xsm:w-1/2 focus:outline-hidden hover:border-primary-300
        hover:bg-primary-50 focus:bg-primary-50 focus:ring-primary-300 group relative flex
        w-full flex-col items-center rounded-lg border border-dashed border-gray-200
        bg-white text-center transition-all duration-300 ease-in focus:ring-1
        focus:ring-offset-2 dark:border-gray-800 dark:bg-gray-600/80
        dark:hover:border-gray-800 dark:hover:bg-gray-400/50 dark:focus:bg-gray-600
        dark:focus:ring-gray-600 sm:hover:scale-[1.01]
      "
      onClick={handleClick}
      type="button"
    >
      <div className="flex flex-1 flex-col justify-center">
        <Image
          alt="Side of the phone to capture"
          className="
            my-10 h-40 w-full object-contain object-center py-4
          "
          height={1200}
          src={thumbnail}
          width={600}
        />
        <div
          className="
            group-hover:border-primary-600 group-hover:text-primary-900
            group-focus:text-primary-800 dark:group-hover:border-primary-600
            dark:group-hover:text-primary-900 dark:group-focus:text-primary-800
            absolute right-2 top-2 flex items-center justify-center rounded-full
            border-2 border-dotted border-gray-300 bg-white px-2 py-1 text-xs
            text-gray-500 dark:border-gray-600 dark:bg-black dark:text-white
            dark:group-hover:bg-white
          "
        >
          <CameraIcon className="h-5 w-5 self-center ltr:mr-1 rtl:ml-1" />
          <Text text={apiTranslations.uploadPending.title} />
        </div>
      </div>

      <div className="w-full flex-col px-4 py-2 text-sm text-gray-500">
        <p
          className="
            group-hover:text-primary-900 group-focus:text-primary-800
            text-body-text dark:group-hover:text-primary-400
            dark:group-focus:text-primary-400 flex w-full
            flex-col items-start pb-1 pt-2 text-sm
          "
        >
          {title ? (
            <span>
              <Text text={title} />
            </span>
          ) : null}

          {titlePostfix ? (
            <span className="text-xs font-light">
              <Text text={titlePostfix} />
            </span>
          ) : null}
        </p>
      </div>
    </button>
  );
}
